<template>
    <div class="form-group form-field">
        <label
            v-if="label || $slots.label"
            :id="fieldId + '-label'"
            :for="fieldId"
            :class="['form-field-label', disabled && 'form-field-label-disabled']"
        >
            <slot name="label">{{ label }}</slot>
            <span v-if="required" class="form-field-required-marker" aria-hidden="true">*</span>
        </label>

        <p v-if="description" :id="descriptionId" class="form-field-description">{{ description }}</p>

        <div :class="['form-field-controls', $slots.actions && 'form-field-controls-inline']">
            <div class="form-field-control">
                <slot :field-id="fieldId" :described-by="describedBy" :required="required" :invalid="!!error"></slot>
            </div>

            <div v-if="$slots.actions" class="form-field-actions">
                <slot name="actions"></slot>
            </div>
        </div>

        <!-- The message of this field takes the place of its help text, so both never compete -->
        <span v-if="error" :id="errorId" class="help-text help-error" role="alert">{{ error }}</span>
        <span v-else-if="helpText" :id="helpTextId" class="help-text">{{ helpText }}</span>
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { getUniqueStringId } from "@/utils/unique-id";

export default defineComponent({
    name: "FormField",

    props: {
        label: String as PropType<string>,
        // Shown between the label and the control, where hint text is read before typing
        description: String as PropType<string>,
        // Shown below the control, for supporting guidance about the field
        helpText: String as PropType<string>,
        // Message about this field, which replaces the help text while it is set.
        // Alerts about the whole form go in a FormError instead
        error: String as PropType<string>,
        required: {
            type: Boolean as PropType<boolean>,
            default: false,
        },
        disabled: {
            type: Boolean as PropType<boolean>,
            default: false,
        },
        id: String as PropType<string>,
    },

    data() {
        return {
            generatedId: getUniqueStringId(),
        };
    },

    computed: {
        fieldId(): string {
            return this.id || this.generatedId;
        },

        helpTextId(): string {
            return this.fieldId + "-help";
        },

        descriptionId(): string {
            return this.fieldId + "-description";
        },

        errorId(): string {
            return this.fieldId + "-error";
        },

        messageId(): string {
            // Only one of the two is rendered at a time, so only one of them is announced
            if (this.error) {
                return this.errorId;
            }

            return this.helpText ? this.helpTextId : "";
        },

        describedBy(): string | undefined {
            const ids = [this.description ? this.descriptionId : "", this.messageId].filter((id) => !!id);
            return ids.length > 0 ? ids.join(" ") : undefined;
        },
    },
});
</script>

<style>
@import "@/style/components/forms/forms.css";
@import "@/style/components/forms/form-field.css";
</style>
